import { Outlet, NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { APP_VERSION } from "../version";
import { useTheme } from "../context/ThemeContext";
import {
  LayoutDashboard, Ticket, Monitor, BarChart3,
  Building2, Users, LogOut, Menu, X, Settings, Code, Wrench, BookOpen, Sun, Moon, Package, ShieldAlert, Database, Server, Gem, Megaphone, FolderKanban, MessageSquare, MonitorPlay, Boxes, Globe, CalendarCheck, Receipt, KeyRound, ShieldCheck, MailWarning, Lock, Target, Newspaper
} from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { getAnalyticsOverview, getLeadGenAccess } from "../api";
import { ROLE_ACCESS } from "../config/roles";
import useIsMobile from "../hooks/useIsMobile";
import TeamsConnectModal from "./TeamsConnectModal";
import AnnouncementBar from "./AnnouncementBar";

const ALL_NAV_ITEMS = [
  { to: "/dashboard",       label: "Dashboard",       icon: LayoutDashboard, page: "dashboard" },
  { to: "/my-day",          label: "My Day",          icon: CalendarCheck,   page: "my-day"    },
  { to: "/tickets",         label: "Tickets",         icon: Ticket,          page: "tickets"   },
  { to: "/clients",         label: "Clients",         icon: Building2,       page: "clients"   },
  { to: "/client-database", label: "Client Database", icon: Database,        page: "client-database" },
  // ownerOnly: shown only when /api/leadgen/access says yes — the server is the gate.
  { to: "/lead-generation", label: "Lead Generation", icon: Target,          page: "lead-generation", ownerOnly: true },
  { to: "/wp-management",   label: "WP Management",   icon: Globe,           page: "wp-management" },
  { to: "/e-editions",      label: "E-Editions",      icon: Newspaper,       page: "e-editions" },
  { to: "/software",        label: "Software",        icon: Boxes,           page: "software" },
  { to: "/api-keys",        label: "MIDTC API",       icon: KeyRound,        page: "api-keys" },
  { to: "/secured-data",    label: "Secured Data",    icon: ShieldCheck,     page: "secured-data" },
  { to: "/send-secure-data", label: "Send Secure Data", icon: Lock,           page: "send-secure-data" },
  { to: "/email-security",  label: "Email Security",  icon: MailWarning,     page: "email-security" },
  { to: "/dev-tickets",     label: "Dev Queue",       icon: Code,            page: "dev-tickets"    },
  { to: "/support-chats",   label: "Support Chats",   icon: MessageSquare,   page: "support-chats"  },
  { to: "/projects",        label: "Projects & Maps", icon: FolderKanban,    page: "infrastructure" },
  { to: "/devices",         label: "Devices",         icon: Monitor,         page: "devices"   },
  { to: "/server-rmm",      label: "Server RMM",      icon: Server,          page: "server-rmm"     },
  { to: "/inventory",       label: "Inventory",       icon: Package,         page: "devices"   },
  { to: "/time-billing",    label: "Time & Billing",  icon: Receipt,         page: "time-billing"    },
  { to: "/analytics",       label: "Analytics",       icon: BarChart3,       page: "analytics"       },
  { to: "/knowledge-base",  label: "Knowledge",       icon: BookOpen,        page: "knowledge-base"  },
  { to: "/agents",          label: "Agents",          icon: Users,           page: "agents"          },
  { to: "/admin-review",    label: "Admin Review",    icon: ShieldAlert,     page: "admin-review"    },
  { to: "/regelis",         label: "Regelis",         icon: Gem,             page: "regelis"         },
  { to: "/settings",        label: "Settings",        icon: Settings,        page: "settings"        },
];

export default function Layout() {
  const { agent, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const isMobile = useIsMobile();
  const [collapsed, setCollapsed] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false); // mobile slide-in nav
  const [alerts, setAlerts]       = useState({ breaches: 0, critical: 0 });
  const [showTeams, setShowTeams] = useState(false);   // Teams Updates connect modal

  const [leadGenAllowed, setLeadGenAllowed] = useState(false);
  useEffect(() => { getLeadGenAccess().then(() => setLeadGenAllowed(true)).catch(() => setLeadGenAllowed(false)); }, []);

  const allowedPages = ROLE_ACCESS[agent?.role] || [];
  const navItems     = ALL_NAV_ITEMS.filter(item => item.ownerOnly ? leadGenAllowed : allowedPages.includes(item.page));

  // Conversation pages (a ticket, a dev ticket) need the width, so there the rail
  // sits as a 64px icon strip and the full menu opens OVER the page on hover, focus
  // or a click of the menu button — the conversation never reflows (Juwan 2026-10-23).
  const location = useLocation();
  const autoCollapse = !isMobile && /^\/(tickets|dev-tickets)\/[^/]+\/?$/.test(location.pathname);
  const [railOver, setRailOver] = useState(false);
  const railTimer = useRef(null);
  const openRail  = (delay = 120) => { clearTimeout(railTimer.current); railTimer.current = setTimeout(() => setRailOver(true), delay); };
  const closeRail = (delay = 220) => { clearTimeout(railTimer.current); railTimer.current = setTimeout(() => setRailOver(false), delay); };
  useEffect(() => { setRailOver(false); }, [location.pathname]);
  useEffect(() => () => clearTimeout(railTimer.current), []);
  useEffect(() => {
    if (!railOver) return;
    const onKey = (e) => { if (e.key === "Escape") setRailOver(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [railOver]);

  // On mobile the sidebar is never "collapsed" — it's either open (full) or hidden.
  const slim = !isMobile && (autoCollapse ? !railOver : collapsed);

  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        const res = await getAnalyticsOverview();
        setAlerts({
          breaches: res.data.tickets?.sla_breaches || 0,
          critical: res.data.devices?.critical     || 0,
        });
      } catch {}
    };
    fetchAlerts();
    const interval = setInterval(fetchAlerts, 60000);
    return () => clearInterval(interval);
  }, []);

  const sidebar = (
    <aside style={{
      ...(isMobile ? {
        position: "fixed", top: 0, bottom: 0, left: 0, zIndex: 200,
        width: 248,
        transform: drawerOpen ? "translateX(0)" : "translateX(-100%)",
        transition: "transform 0.22s ease",
        boxShadow: drawerOpen ? "8px 0 40px rgba(0,0,0,0.5)" : "none",
      } : autoCollapse ? {
        // In-flow 64px strip; when open it is lifted out of the flow (absolute) so the
        // page underneath keeps its width.
        width: railOver ? 224 : 64,
        transition: "width 0.18s ease",
        position: "absolute", top: 0, bottom: 0, left: 0, zIndex: 300,
        boxShadow: railOver ? "16px 0 40px rgba(0,0,0,0.38)" : "none",
        backdropFilter: "var(--t-backdrop, none)",
      } : {
        width: slim ? 64 : 224,
        transition: "width 0.2s ease",
        position: "relative",
      }),
      background: "var(--t-rail-bg, var(--t-grad-sidebar))",
      borderRight: "1px solid var(--t-rail-border, rgba(148,163,184,0.11))",
      display: "flex",
      flexDirection: "column",
      flexShrink: 0,
    }}>

      {/* Top shimmer line */}
      <div style={{ position:"absolute", top:0, left:0, right:0, height:1, background:"linear-gradient(90deg,transparent,var(--t-accent,#4f6fff),transparent)", opacity:0.5, pointerEvents:"none" }} />

      {/* Logo */}
      <div style={{
        padding: slim ? "16px 12px" : "16px 16px 14px",
        borderBottom: "1px solid var(--t-rail-border, var(--t-border-soft))",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}>
        {!slim && (
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <div style={{
              width:36, height:36, borderRadius:9, flexShrink:0,
              background:"var(--t-accent-grad, linear-gradient(135deg,#4f6fff 0%,#7b61ff 100%))",
              display:"flex", alignItems:"center", justifyContent:"center",
              fontSize:14, fontWeight:800, color:"#fff",
              boxShadow:"var(--t-accent-glow, 0 0 18px rgba(79,111,255,0.45)), inset 0 1px 0 rgba(255,255,255,0.2)",
              overflow:"hidden",
            }}>
              <img src="/logo.jpg" alt="M" style={{ width:"100%", height:"100%", objectFit:"cover", borderRadius:9 }} onError={e => { e.target.style.display="none"; }} />
            </div>
            <div>
              <div style={{ fontWeight:700, fontSize:14, color:"var(--t-rail-text-strong, var(--t-text-primary))", letterSpacing:-0.2 }}>MIDTC</div>
              <div style={{ fontSize:10, color:"var(--t-rail-dim, var(--t-text-faint))", textTransform:"uppercase", letterSpacing:"0.05em" }}>MSP Platform</div>
            </div>
          </div>
        )}
        {slim && (
          <div style={{
            width:32, height:32, borderRadius:8,
            background:"var(--t-accent-grad, linear-gradient(135deg,#4f6fff,#7b61ff))",
            boxShadow:"var(--t-accent-glow, 0 0 18px rgba(79,111,255,0.45))",
            overflow:"hidden", flexShrink:0,
          }}>
            <img src="/logo.jpg" alt="M" style={{ width:"100%", height:"100%", objectFit:"cover", borderRadius:8 }} onError={e => { e.target.style.display="none"; }} />
          </div>
        )}
        <button
          onClick={() => isMobile ? setDrawerOpen(false) : autoCollapse ? setRailOver(o => !o) : setCollapsed(!collapsed)}
          aria-label={slim ? "Open the menu" : "Close the menu"}
          title={slim ? "Open the menu" : "Close the menu"}
          style={{ background:"none", border:"none", color:"var(--t-rail-dim, var(--t-text-faint))", padding:4, borderRadius:4, display:"flex", cursor:"pointer" }}
        >
          {slim ? <Menu size={16} /> : <X size={16} />}
        </button>
      </div>

      {/* Alert bar */}
      {!slim && alerts.breaches > 0 && (
        <div style={{ padding:"8px 10px 0" }}>
          <div style={{
            padding:"7px 11px",
            background:"linear-gradient(135deg,rgba(127,29,29,0.5),rgba(127,29,29,0.2))",
            border:"1px solid rgba(239,68,68,0.3)", borderRadius:8,
            fontSize:11, color:"#fca5a5",
            display:"flex", alignItems:"center", gap:7,
            boxShadow:"0 0 14px rgba(239,68,68,0.1)",
          }}>
            <div style={{ width:7, height:7, borderRadius:"50%", background:"#ef4444", boxShadow:"0 0 7px #ef4444", flexShrink:0 }} />
            {alerts.breaches} SLA Breach{alerts.breaches > 1 ? "es" : ""}
          </div>
        </div>
      )}

      {/* Role pill */}
      {!slim && (
        <div style={{ padding:"7px 10px 0" }}>
          <span style={{
            display:"inline-flex", fontSize:10, fontWeight:700,
            textTransform:"uppercase", letterSpacing:"0.06em",
            padding:"3px 10px", borderRadius:99,
            background:"var(--t-accent-soft, rgba(79,111,255,0.15))", color:"var(--t-accent-2, #818cf8)",
            border:"1px solid var(--t-accent-soft, rgba(79,111,255,0.25))",
          }}>
            {agent?.role}
          </span>
        </div>
      )}

      {/* Nav */}
      <nav style={{ flex:1, padding:"5px 8px", display:"flex", flexDirection:"column", gap:2, overflowY:"auto" }}>
        {navItems.map(({ to, label, icon: Icon, page }) => (
          <NavLink
            key={to}
            to={to}
            onClick={() => setDrawerOpen(false)}
            style={({ isActive }) => ({
              display:        "flex",
              alignItems:     "center",
              gap:            10,
              padding:        slim ? "10px 12px" : (isMobile ? "12px 11px" : "9px 11px"),
              borderRadius:   8,
              // Accent-driven so the sidebar re-skins with the user's brand
              // theme. The inactive grey stays fixed: the sidebar is dark in
              // every theme/mode, so a neutral grey is correct throughout.
              color:          isActive ? "var(--t-accent-2, #818cf8)" : (page === "settings" ? "var(--t-accent, #7b61ff)" : "var(--t-rail-text, #94a3b8)"),
              background:     isActive ? "linear-gradient(120deg,var(--t-accent-soft,rgba(79,111,255,0.22)),var(--t-accent-softer,rgba(79,111,255,0.07)))" : "transparent",
              fontWeight:     isActive ? 600 : 400,
              fontSize:       13,
              border:         isActive ? "1px solid var(--t-accent-soft, rgba(79,111,255,0.22))" : "1px solid transparent",
              boxShadow:      isActive ? "inset 3px 0 0 var(--t-accent, #6366f1)" : "none",
              justifyContent: slim ? "center" : "flex-start",
              textDecoration: "none",
              transition:     "all 0.15s",
            })}
          >
            <Icon size={16} style={{ flexShrink:0 }} />
            {!slim && <span>{label}</span>}
          </NavLink>
        ))}
        {/* Ad Server — admin-only external link (new tab) to troubleshoot ad-server issues */}
        {["admin", "manager"].includes(agent?.role) && (
          <a
            href="https://adserver.midtc.com/admin/"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setDrawerOpen(false)}
            title="Open the MIDTC Ad Server (new tab)"
            style={{
              display:"flex", alignItems:"center", gap:10,
              padding: slim ? "10px 12px" : (isMobile ? "12px 11px" : "9px 11px"),
              borderRadius:8, color:"var(--t-rail-text, #94a3b8)", background:"transparent",
              fontWeight:400, fontSize:13, border:"1px solid transparent",
              justifyContent: slim ? "center" : "flex-start",
              textDecoration:"none", transition:"all 0.15s",
            }}
          >
            <Megaphone size={16} style={{ flexShrink:0 }} />
            {!slim && <span>Ad Server</span>}
          </a>
        )}
      </nav>

      {/* Agent info + logout */}
      <div style={{ padding: slim ? "12px 8px" : "11px 10px", borderTop:"1px solid var(--t-rail-border, var(--t-border-soft))" }}>
        {!slim && (
          <div style={{
            display:"flex", alignItems:"center", gap:9, marginBottom:5,
            padding:"8px 10px", borderRadius:8,
            background:"var(--t-rail-card-bg, rgba(18,30,51,0.9))", border:"1px solid var(--t-rail-border, var(--t-border-soft))",
          }}>
            <div style={{
              width:29, height:29, background:"var(--t-accent-grad, linear-gradient(135deg,#4f6fff,#7b61ff))", borderRadius:"50%",
              display:"flex", alignItems:"center", justifyContent:"center",
              fontSize:11, fontWeight:700, color:"#fff", flexShrink:0,
              boxShadow:"var(--t-accent-glow, 0 0 10px rgba(79,111,255,0.3))",
            }}>
              {agent?.firstName?.[0]}{agent?.lastName?.[0]}
            </div>
            <div style={{ overflow:"hidden" }}>
              <div style={{ fontSize:12, fontWeight:600, color:"var(--t-rail-text-strong, var(--t-text-primary))", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
                {agent?.firstName} {agent?.lastName}
              </div>
              <div style={{ fontSize:10, color:"var(--t-rail-dim, var(--t-text-faint))", textTransform:"capitalize" }}>{agent?.role}</div>
            </div>
          </div>
        )}
        <div style={{ display:"flex", gap:4, marginBottom:4 }}>
          <button
            onClick={toggleTheme}
            title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            style={{
              flex: slim ? "1 1 auto" : "0 0 auto",
              display:"flex", alignItems:"center", justifyContent:"center", gap:6,
              padding:"7px 10px", borderRadius:8, background:"var(--t-accent-softer, rgba(79,111,255,0.1))",
              border:"1px solid var(--t-accent-soft, rgba(79,111,255,0.2))", color:"var(--t-accent-2, #818cf8)",
              fontSize:12, cursor:"pointer", fontFamily:"inherit", transition:"all 0.15s",
            }}
            onMouseEnter={e => { e.currentTarget.style.background="var(--t-accent-soft, rgba(79,111,255,0.18))"; }}
            onMouseLeave={e => { e.currentTarget.style.background="var(--t-accent-softer, rgba(79,111,255,0.1))"; }}
          >
            {theme === "dark" ? <Sun size={14}/> : <Moon size={14}/>}
            {!slim && <span style={{ fontSize:11, fontWeight:500 }}>{theme === "dark" ? "Light" : "Dark"}</span>}
          </button>
          <button
            onClick={() => setShowTeams(true)}
            title="Teams Updates — get ticket/dev/project notifications in Microsoft Teams"
            style={{
              flex:"1 1 auto",
              display:"flex", alignItems:"center", justifyContent:"center", gap:6,
              padding:"7px 10px", borderRadius:8, background:"var(--t-accent-softer, rgba(79,111,255,0.1))",
              border:"1px solid var(--t-accent-soft, rgba(79,111,255,0.2))", color:"var(--t-accent-2, #818cf8)",
              fontSize:12, cursor:"pointer", fontFamily:"inherit", transition:"all 0.15s",
            }}
            onMouseEnter={e => { e.currentTarget.style.background="var(--t-accent-soft, rgba(79,111,255,0.18))"; }}
            onMouseLeave={e => { e.currentTarget.style.background="var(--t-accent-softer, rgba(79,111,255,0.1))"; }}
          >
            <MessageSquare size={14}/>
            {!slim && <span style={{ fontSize:11, fontWeight:500 }}>Teams</span>}
          </button>
        </div>
        <button
          onClick={logout}
          style={{
            width:"100%", display:"flex", alignItems:"center", gap:8,
            justifyContent: slim ? "center" : "flex-start",
            padding:"7px 10px", borderRadius:8, background:"none", border:"none",
            color:"var(--t-rail-dim, var(--t-text-faint))", fontSize:12, transition:"all 0.15s", cursor:"pointer",
            fontFamily:"inherit",
          }}
          onMouseEnter={e => { e.currentTarget.style.background="rgba(248,113,113,0.08)"; e.currentTarget.style.color="#f87171"; }}
          onMouseLeave={e => { e.currentTarget.style.background="none"; e.currentTarget.style.color="var(--t-rail-dim, #56677e)"; }}
        >
          <LogOut size={15} />
          {!slim && <span>Sign Out</span>}
        </button>
        {!slim && <div style={{ fontSize:10.5, color:"var(--t-rail-dim, var(--t-text-faint))", padding:"4px 10px 0" }}>Version {APP_VERSION}</div>}
      </div>

    </aside>
  );

  return (
    <div className="app-shell" style={{ display:"flex", flexDirection: isMobile ? "column" : "row", overflow:"hidden", background:"var(--t-page-glow, transparent), var(--t-page)", color:"var(--t-text-primary)" }}>

      {/* ── Mobile top bar ──────────────────────────────────── */}
      {isMobile && (
        <header style={{
          display:"flex", alignItems:"center", gap:12, padding:"10px 14px",
          background:"var(--t-rail-bg, var(--t-grad-sidebar))", borderBottom:"1px solid var(--t-rail-border, rgba(148,163,184,0.11))",
          color:"var(--t-rail-text-strong, var(--t-text-primary))",
          flexShrink:0, position:"sticky", top:0, zIndex:100,
        }}>
          <button
            onClick={() => setDrawerOpen(true)}
            style={{ background:"none", border:"none", color:"var(--t-rail-text-strong, var(--t-text-primary))", padding:6, display:"flex", cursor:"pointer" }}
          >
            <Menu size={22} />
          </button>
          <div style={{
            width:28, height:28, borderRadius:7, overflow:"hidden", flexShrink:0,
            background:"var(--t-accent-grad, linear-gradient(135deg,#4f6fff,#7b61ff))",
          }}>
            <img src="/logo.jpg" alt="M" style={{ width:"100%", height:"100%", objectFit:"cover" }} onError={e => { e.target.style.display="none"; }} />
          </div>
          <div style={{ fontWeight:700, fontSize:14 }}>MIDTC</div>
          {alerts.breaches > 0 && (
            <div style={{ marginLeft:"auto", display:"flex", alignItems:"center", gap:6, fontSize:11, color:"#fca5a5" }}>
              <div style={{ width:7, height:7, borderRadius:"50%", background:"#ef4444", boxShadow:"0 0 7px #ef4444" }} />
              {alerts.breaches} SLA
            </div>
          )}
        </header>
      )}

      {/* ── Mobile drawer backdrop ──────────────────────────── */}
      {isMobile && drawerOpen && (
        <div
          onClick={() => setDrawerOpen(false)}
          style={{ position:"fixed", inset:0, background:"rgba(2,6,23,0.6)", zIndex:150 }}
        />
      )}

      {/* ── Sidebar (desktop inline / mobile drawer / overlay strip) ── */}
      {autoCollapse ? (
        <div
          style={{ position:"relative", width:64, flexShrink:0, zIndex:300 }}
          onMouseEnter={() => openRail()}
          onMouseLeave={() => closeRail()}
          onFocus={() => openRail(0)}
          onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) closeRail(0); }}
        >
          {sidebar}
        </div>
      ) : sidebar}

      {/* ── Main content ────────────────────────────────────── */}
      <main style={{ flex:1, minWidth:0, minHeight:0, overflow:"auto", display:"flex", flexDirection:"column" }}>
        {/* System announcements on every page; the Dashboard shows them itself, right above Today's boost */}
        {!/^\/dashboard\/?$/.test(location.pathname) && (
          <AnnouncementBar refreshKey={location.pathname} style={{ margin: isMobile ? "12px 14px 0" : "16px 22px 0", flexShrink:0 }} />
        )}
        <Outlet />
      </main>

      {showTeams && <TeamsConnectModal onClose={() => setShowTeams(false)} />}

    </div>
  );
}
